import React, { useState } from "react";
import {
  Button,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  Text,
  Textarea,
} from "@fluentui/react-components";
import { Add16Regular, Dismiss16Regular } from "@fluentui/react-icons";
import type { StyleProfile } from "../settings";
import { MAX_STYLE_PROFILE_BYTES } from "../settings";

const EMPTY_PROFILE: StyleProfile = {
  tone: "",
  length: "",
  greeting: "",
  signature: "",
  language: "",
  dosAndDonts: [],
};

const TONE_OPTIONS = ["Neutral", "Formal", "Friendly"];
const LENGTH_OPTIONS = ["Short", "Medium", "Detailed"];

export interface StyleProfileEditorProps {
  /** Current profile, or null when none has been saved yet (renders empty). */
  value: StyleProfile | null;
  /** Called with the full next profile on every edit — parent owns persistence. */
  onChange: (profile: StyleProfile) => void;
}

/**
 * Controlled editor for the persistent {@link StyleProfile}. Emits the whole
 * next profile on each change; the parent (SettingsPanel) holds state and saves
 * via `saveStyleProfile` (which enforces the byte budget). Shows a live byte
 * count and a budget error so the user is warned before a save can fail.
 */
export function StyleProfileEditor({ value, onChange }: StyleProfileEditorProps): JSX.Element {
  const profile = value ?? EMPTY_PROFILE;
  const [newRule, setNewRule] = useState("");

  const byteLength = new TextEncoder().encode(JSON.stringify(profile)).length;
  const overBudget = byteLength > MAX_STYLE_PROFILE_BYTES;

  const update = (patch: Partial<StyleProfile>) => onChange({ ...profile, ...patch });

  const addRule = () => {
    const trimmed = newRule.trim();
    if (!trimmed) return;
    update({ dosAndDonts: [...profile.dosAndDonts, trimmed] });
    setNewRule("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <Field label="Tone">
        <Dropdown
          aria-label="Tone"
          placeholder="Select a tone"
          value={profile.tone}
          selectedOptions={profile.tone ? [profile.tone] : []}
          onOptionSelect={(_, d) => update({ tone: (d.optionValue as string) ?? "" })}
        >
          {TONE_OPTIONS.map((t) => (
            <Option key={t} value={t}>
              {t}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Field label="Length">
        <Dropdown
          aria-label="Length"
          placeholder="Select a length"
          value={profile.length}
          selectedOptions={profile.length ? [profile.length] : []}
          onOptionSelect={(_, d) => update({ length: (d.optionValue as string) ?? "" })}
        >
          {LENGTH_OPTIONS.map((l) => (
            <Option key={l} value={l}>
              {l}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Field label="Greeting">
        <Input value={profile.greeting} onChange={(_, d) => update({ greeting: d.value })} />
      </Field>
      <Field label="Signature">
        <Textarea value={profile.signature} onChange={(_, d) => update({ signature: d.value })} />
      </Field>
      <Field label="Language">
        <Input value={profile.language} onChange={(_, d) => update({ language: d.value })} />
      </Field>
      <Field label="Do's and don'ts">
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {profile.dosAndDonts.map((rule, i) => (
            <div key={`${rule}-${i}`} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <Text style={{ flex: 1 }}>{rule}</Text>
              <Button
                size="small"
                appearance="subtle"
                icon={<Dismiss16Regular />}
                aria-label={`Remove ${rule}`}
                onClick={() => update({ dosAndDonts: profile.dosAndDonts.filter((_, j) => j !== i) })}
              />
            </div>
          ))}
          <div style={{ display: "flex", gap: 4 }}>
            <Input
              aria-label="New rule"
              placeholder="Add a rule…"
              value={newRule}
              onChange={(_, d) => setNewRule(d.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addRule();
                }
              }}
            />
            <Button size="small" icon={<Add16Regular />} aria-label="Add rule" onClick={addRule}>
              Add
            </Button>
          </div>
        </div>
      </Field>
      <Text size={100} style={{ opacity: 0.7 }}>
        {byteLength} / {MAX_STYLE_PROFILE_BYTES} bytes
      </Text>
      {overBudget && (
        <MessageBar intent="error">
          <MessageBarBody>Writing style is too large. Please shorten it.</MessageBarBody>
        </MessageBar>
      )}
    </div>
  );
}
